import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { subscriptionApi } from '@/api/subscriptions';
import { PLANS, type PlanData } from '@/data/plans';

const faqs = [
  { q: 'Posso cancelar a qualquer momento?', a: 'Sim. Não há fidelidade. Você pode cancelar quando quiser e continua com acesso até o fim do período pago.' },
  { q: 'O plano Free tem limite de tempo?', a: 'Não. O plano Free é gratuito para sempre, com as limitações descritas.' },
  { q: 'Posso mudar de plano depois?', a: 'Sim. Você pode fazer upgrade ou downgrade a qualquer momento. O valor é ajustado proporcionalmente.' },
  { q: 'Como funciona o plano Group?', a: 'O administrador cria o grupo e convida até 4 membros adicionais. Todos compartilham a mesma biblioteca de músicas.' },
  { q: 'Há desconto anual?', a: 'Sim! Pagando anualmente você economiza cerca de 17% em relação ao plano mensal.' },
];

const PlansPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { plans: apiPlans, loading: loadingPlans, error: plansError, loadPlans } = useSubscription();

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [trialUsed, setTrialUsed] = useState<boolean | null>(null);
  const [startingTrial, setStartingTrial] = useState(false);
  const [trialSuccess, setTrialSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      subscriptionApi.getTrialStatus()
        .then((t) => setTrialUsed(t.trial_used))
        .catch(() => setTrialUsed(false));
    }
  }, [isAuthenticated]);

  const handleStartTrial = async () => {
    if (!isAuthenticated) {
      navigate('/auth?mode=register&redirect=/plans');
      return;
    }
    setStartingTrial(true);
    try {
      await subscriptionApi.startTrial();
      setTrialSuccess(true);
      setTrialUsed(true);
    } catch {
      // Trial provavelmente já usado, silencia
    } finally {
      setStartingTrial(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Map API plans with local plan data
  const plansWithPrices = Object.values(PLANS).map((planData) => {
    const apiPlan = apiPlans.find((p) => p.code === planData.code);
    return {
      ...planData,
      price_cents: apiPlan?.price_cents ?? 0,
    };
  });

  const handleSelectPlan = (planCode: string) => {
    if (!isAuthenticated) {
      navigate(`/auth?mode=register&redirect=/checkout?plan=${planCode}`);
    } else {
      navigate(`/checkout?plan=${planCode}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-10 text-center">
        <span className="inline-block text-[#C8A96E] text-xs uppercase tracking-widest mb-4">Planos & Preços</span>
        <h1 className="text-5xl md:text-6xl font-light text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          Escolha seu <strong>plano</strong>
        </h1>
        <p className="text-white/40 text-base max-w-md mx-auto">
          Comece gratuitamente e escale conforme seu ministério cresce.
        </p>
      </section>

      {/* Trial Banner */}
      {!trialUsed && (
        <section className="px-6 md:px-10 pb-8">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-[#C8A96E]/20 bg-gradient-to-r from-[#C8A96E]/10 to-transparent px-8 py-6 flex flex-col md:flex-row items-center gap-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-12 h-12 rounded-full bg-[#C8A96E]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-gift-line text-2xl text-[#C8A96E]"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-lg">7 dias grátis, sem cartão</p>
                  <p className="text-white/50 text-sm">Experimente todos os recursos dos planos pagos sem compromisso.</p>
                </div>
              </div>
              {trialSuccess ? (
                <div className="flex items-center gap-2 text-green-400 font-semibold">
                  <i className="ri-check-line text-xl"></i>
                  Trial ativado! Aproveite.
                </div>
              ) : (
                <button
                  onClick={handleStartTrial}
                  disabled={startingTrial}
                  className="px-6 py-3 rounded-xl font-semibold text-sm bg-[#C8A96E] text-[#0A0A0F] hover:bg-[#D4B882] transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer flex-shrink-0"
                >
                  {startingTrial ? 'Iniciando...' : 'Experimentar grátis por 7 dias'}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Plans Grid */}
      <section className="px-6 md:px-10 pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {loadingPlans ? (
            <div className="col-span-full text-center py-12">
              <p className="text-white/50">Carregando planos...</p>
            </div>
          ) : plansError ? (
            <div className="col-span-full text-center py-12">
              <p className="text-red-400 mb-4">Erro ao carregar planos</p>
              <button
                onClick={() => loadPlans()}
                className="px-4 py-2 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882]"
              >
                Tentar novamente
              </button>
            </div>
          ) : plansWithPrices.length > 0 ? (
            plansWithPrices.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                onSelect={handleSelectPlan}
                isAuthenticated={isAuthenticated}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-white/50">Nenhum plano disponível</p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-10 pb-28">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-light text-white text-center mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>
            Perguntas <strong>frequentes</strong>
          </h2>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#111111] border border-white/5 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                >
                  <span className="text-white text-sm font-medium">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-white/40 text-sm`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-white/50 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 pb-20 text-center">
        <p className="text-white/30 text-sm mb-4">Ainda tem dúvidas?</p>
        <button
          onClick={() => {
            if (!isAuthenticated) {
              navigate('/auth?mode=register');
            } else {
              navigate('/subscription-dashboard');
            }
          }}
          className="inline-flex items-center gap-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold px-8 py-3.5 rounded-full hover:bg-[#D4B87A] transition-colors whitespace-nowrap cursor-pointer"
        >
          {isAuthenticated ? 'Ver minha assinatura' : 'Começar gratuitamente'}
          <i className="ri-arrow-right-line"></i>
        </button>
      </section>

      <Footer />
    </div>
  );
};

interface PlanCardProps {
  plan: PlanData & { price_cents: number };
  onSelect: (planCode: string) => void;
  isAuthenticated: boolean;
}

const PlanCard = ({ plan, onSelect, isAuthenticated }: PlanCardProps) => {
  const displayPrice = plan.price_cents === 0 ? 'Grátis' : `R$ ${(plan.price_cents / 100).toFixed(2).replace('.', ',')}`;

  return (
    <div
      className={`relative rounded-2xl p-8 flex flex-col transition-all duration-300 ${
        plan.highlight
          ? 'bg-[#C8A96E] text-[#0A0A0F]'
          : 'bg-[#111111] border border-white/5 text-white hover:border-white/15'
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0A0A0F] text-[#C8A96E] text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">
          Mais popular
        </div>
      )}

      <div className="mb-6">
        <p className={`text-xs uppercase tracking-widest mb-3 ${plan.highlight ? 'text-[#0A0A0F]/60' : 'text-white/40'}`}>
          {plan.name}
        </p>
        <div className="flex items-end gap-2 mb-2">
          <span className="text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            {displayPrice}
          </span>
          {plan.price_cents > 0 && (
            <span className={`text-sm mb-1 ${plan.highlight ? 'text-[#0A0A0F]/60' : 'text-white/40'}`}>
              {plan.period}
            </span>
          )}
        </div>
        <p className={`text-sm leading-relaxed ${plan.highlight ? 'text-[#0A0A0F]/70' : 'text-white/40'}`}>
          {plan.description}
        </p>
      </div>

      <div className="flex-1 mb-8">
        <div className="flex flex-col gap-3">
          {plan.features.map((f) => (
            <div key={f} className="flex items-center gap-3">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-check-line text-sm ${plan.highlight ? 'text-[#0A0A0F]' : 'text-[#C8A96E]'}`}></i>
              </div>
              <span className={`text-sm ${plan.highlight ? 'text-[#0A0A0F]' : 'text-white/70'}`}>{f}</span>
            </div>
          ))}
          {plan.disabled.map((f) => (
            <div key={f} className="flex items-center gap-3 opacity-40">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-close-line text-sm"></i>
              </div>
              <span className="text-sm line-through">{f}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => onSelect(plan.code)}
        className={`w-full py-3 rounded-full text-sm font-semibold text-center transition-all duration-300 whitespace-nowrap cursor-pointer ${
          plan.highlight
            ? 'bg-[#0A0A0F] text-white hover:bg-[#1A1A1A]'
            : 'border border-white/20 text-white hover:border-white/40 hover:bg-white/5'
        }`}
      >
        {plan.code === 'free'
          ? 'Começar grátis'
          : isAuthenticated
            ? `Assinar ${plan.name}`
            : `Assinar ${plan.name}`}
      </button>
    </div>
  );
};

export default PlansPage;
